import { DATE_FORMAT, useDateService } from 'framework/utils';
import { calculateFulfillmentPercentage, getStatusColor } from './structureComparisonUtils';
import { Typography, Box, Grid2 as Grid } from '@mui/material';
import Radar from '@mui/icons-material/Radar';
import GroupsIcon from '@mui/icons-material/Groups';
import ComplianceTrendChart from './charts/ComplianceTrendChart';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'framework/ui/mui-builder';
import { EMPLOYEES_PATH } from 'domain/shared/employee';

const StructureComparisonCard = ({ rule }: { rule: any }) => {
  const { formatDate, getMomentDateObj } = useDateService();
  const navigate = useNavigate();

  const buildFilterUrl = () => {
    const filter: any = {
      workingStatus: { in: ['ACTIVE', 'RELOCATION'] },
    };

    if (rule.positionId) {
      filter['position.id'] = { in: [`${rule.positionId}`] };
    }

    if (rule.technologyId) {
      filter['specializationInfo.technology.id'] = { in: [rule.technologyId] };
    }

    if (rule.competencyLevel) {
      filter['specializationInfo.competencyLevel'] = { in: [rule.competencyLevel] };
    }

    const encodedFilter = encodeURIComponent(JSON.stringify(filter));
    return `${EMPLOYEES_PATH}?filter=${encodedFilter}`;
  };

  const handleTitleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const url = buildFilterUrl();
    navigate(url);
  };

  const formatTrendData = (trendData: Record<string, number>, capacity: number) => {
    return Object.entries(trendData)
      .map(([date, actualValue]) => {
        const percentage = calculateFulfillmentPercentage(capacity, actualValue);
        const absolutePercentage = Math.abs(percentage);

        return {
          date,
          actualValue,
          percentage,
          absolutePercentage,
          isOverCapacity: actualValue > capacity,
          formattedDate: formatDate(date, DATE_FORMAT.MONTH_DAY_WEEKDAY),
        };
      })
      .sort((a, b) => {
        const dateA = getMomentDateObj(a.date);
        const dateB = getMomentDateObj(b.date);
        return dateA.valueOf() - dateB.valueOf();
      });
  };

  const maxValue = Math.max(rule.capacity, rule.actual);
  const targetPercent = (rule.capacity / maxValue) * 100;
  const actualPercent = (rule.actual / maxValue) * 100;
  const displayPercentage =
    rule.fulfillmentPercentage < 0 ? `${Math.abs(rule.fulfillmentPercentage)}%` : `${rule.fulfillmentPercentage}%`;
  const trendData = rule.trend ? formatTrendData(rule.trend, rule.capacity) : [];

  return (
    <Grid size={{ xs: 12, sm: 4, md: 3 }} sx={{ minWidth: '300px' }} className="rule-card">
      <Box
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 1,
          p: '20px',
          boxShadow: '0 2px 4px var(--mui-palette-common-shadowDefault)',
        }}
      >
        <Box mb={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <Typography
              variant="h6"
              sx={{
                m: 0,
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary',
                },
              }}
              onClick={handleTitleClick}
            >
              {rule.position
                ? `${rule.position}${rule.competencyLevel ? ` (${rule.competencyLevel})` : ''}`
                : rule.competencyLevel || 'General Capacity'}
            </Typography>
            <Box sx={{ borderRadius: '20px' }}>
              <Typography variant="subtitle1" color={getStatusColor(rule.fulfillmentPercentage)} pl={1}>
                {displayPercentage}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2">{rule.technology || ''}</Typography>
        </Box>
        <Box sx={{ mb: 2, gap: 2, display: 'flex' }}>
          <Box sx={{ display: 'flex', fontWeight: 500, alignItems: 'center', gap: 1 }}>
            <Tooltip title="Target capacity">
              <Radar />
            </Tooltip>
            {rule.capacity}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 500, gap: 1 }}>
            <Tooltip title="Current capacity">
              <GroupsIcon />
            </Tooltip>
            {rule.actual}
          </Box>
        </Box>
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: '4px',
              fontSize: '10px',
            }}
          >
            <Typography variant="caption" color="textSecondary">
              0
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {maxValue}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: `${targetPercent}%`,
                backgroundColor: 'primary.main',
                height: '20px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="caption" color="white" pl={1}>
                Target
              </Typography>
            </Box>
            <Box
              sx={{
                width: `${actualPercent}%`,
                backgroundColor: 'var(--mui-palette-background-primary)',
                height: '20px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography component="span" variant="caption" color="primary.main" sx={{ pl: 1 }}>
                Actual
              </Typography>
            </Box>
          </Box>
        </Box>
        <ComplianceTrendChart trendData={trendData} />
      </Box>
    </Grid>
  );
};

export default StructureComparisonCard;
